import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { reservationService } from '../../services/reservationService';
import { classService } from '../../services/classService';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Select, Label, FormGroup, TextArea } from '../../components/Input';
import { Loader } from '../../components/Loader';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: ${colors.primary};
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  display: none;
  background: white;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px ${colors.shadow};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const MobileCardInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

const MobileCardLabel = styled.span`
  font-size: 12px;
  color: ${colors.textLight};
  font-weight: 500;
`;

const MobileCardValue = styled.span`
  font-size: 14px;
  color: ${colors.text};
  font-weight: 400;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Th = styled.th`
  background-color: ${colors.primary};
  color: white;
  padding: 15px;
  text-align: left;
  font-weight: 500;

  @media (max-width: 768px) {
    padding: 10px 8px;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
  }
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid ${colors.border};

  @media (max-width: 768px) {
    padding: 10px 8px;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
  }
`;

const Tr = styled.tr`
  &:hover {
    background-color: ${colors.background};
  }
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    if (props.status === 'approved') return colors.success;
    if (props.status === 'rejected' || props.status === 'cancelled') return colors.error;
    return colors.warning;
  }};
  color: white;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const statusLabels = {
  pending: 'Bekliyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  cancelled: 'İptal Edildi'
};

export const AdminReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'reject' or 'move'
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [moveToClassId, setMoveToClassId] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const normalizeStatus = (status) => (status || '').toLowerCase().trim();

  const deriveStatusKey = (reservation) => {
    const normalized = normalizeStatus(reservation.status);

    if (reservation.cancelledAt || normalized.includes('cancel')) {
      return 'cancelled';
    }

    if (normalized) {
      if (statusLabels[normalized]) {
        return normalized;
      }
      return normalized;
    }

    if (reservation.cancelledAt) {
      return 'cancelled';
    }

    return 'pending';
  };

  const getStatusLabel = (reservation) => {
    const key = deriveStatusKey(reservation);
    return statusLabels[key] || reservation.status || '-';
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const filterStatus = filter === 'all' ? undefined : filter;
      const [reservationsData, classesData] = await Promise.all([
        reservationService.getAllReservationsWithDetails(), // Use optimized endpoint
        classService.getAllClasses()
      ]);
      
      // Filter reservations by status if needed
      let filteredReservations = filterStatus 
        ? reservationsData.filter(r => r.status === filterStatus)
        : reservationsData;
      
      // Sort reservations by date (newest first)
      filteredReservations.sort((a, b) => {
        const dateA = a.classDetails?.date || '';
        const dateB = b.classDetails?.date || '';
        const timeA = a.classDetails?.time || '';
        const timeB = b.classDetails?.time || '';
        
        // Parse dates - try multiple formats
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          
          // Try YYYY-MM-DD format
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return new Date(dateStr);
          }
          
          // Try DD.MM.YYYY format
          if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            const [day, month, year] = dateStr.split('.');
            return new Date(`${year}-${month}-${day}`);
          }
          
          // Try DD/MM/YYYY format
          if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [day, month, year] = dateStr.split('/');
            return new Date(`${year}-${month}-${day}`);
          }
          
          // Default parse
          return new Date(dateStr) || new Date(0);
        };
        
        // Parse times (HH:MM format)
        const parseTime = (timeStr) => {
          if (!timeStr) return { hours: 0, minutes: 0 };
          const [hours, minutes] = timeStr.split(':').map(Number);
          return { hours: hours || 0, minutes: minutes || 0 };
        };
        
        const dateObjA = parseDate(dateA);
        const dateObjB = parseDate(dateB);
        
        // Compare dates first
        if (dateObjA.getTime() !== dateObjB.getTime()) {
          return dateObjB.getTime() - dateObjA.getTime(); // Newest first
        }
        
        // If dates are same, compare times
        const timeA_obj = parseTime(timeA);
        const timeB_obj = parseTime(timeB);
        const timeA_minutes = timeA_obj.hours * 60 + timeA_obj.minutes;
        const timeB_minutes = timeB_obj.hours * 60 + timeB_obj.minutes;
        
        return timeB_minutes - timeA_minutes; // Newest time first
      });
        
      setReservations(filteredReservations);
      setClasses(classesData);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await reservationService.approveReservation(id);
      toast.success('Rezervasyon onaylandı');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Onaylama başarısız');
    }
  };

  const openRejectModal = (reservation) => {
    setSelectedReservation(reservation);
    setRejectReason('');
    setModalType('reject');
    setModalOpen(true);
  };

  const handleReject = async () => {
    try {
      await reservationService.rejectReservation(selectedReservation.id, rejectReason);
      toast.success('Rezervasyon reddedildi');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Reddetme başarısız');
    }
  };

  const openMoveModal = (reservation) => {
    setSelectedReservation(reservation);
    setMoveToClassId('');
    setModalType('move');
    setModalOpen(true);
  };

  const handleMove = async () => {
    if (!moveToClassId) {
      toast.error('Lütfen bir ders seçin');
      return;
    }

    try {
      await reservationService.moveReservation(selectedReservation.id, moveToClassId);
      toast.success('Rezervasyon taşındı');
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Taşıma başarısız');
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Bu rezervasyonu iptal etmek istediğinize emin misiniz?')) {
      try {
        await reservationService.cancelReservation(id);
        toast.success('Rezervasyon iptal edildi');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'İptal başarısız');
      }
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Header>
        <Title>Rezervasyon Yönetimi</Title>
        <Filters>
          <Label>Filtre:</Label>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Tümü</option>
            <option value="pending">Bekleyenler</option>
            <option value="approved">Onaylananlar</option>
            <option value="rejected">Reddedilenler</option>
          </Select>
        </Filters>
      </Header>

      <Table>
        <thead>
          <tr>
            <Th>Üye</Th>
            <Th>Ders</Th>
            <Th>Tarih & Saat</Th>
            <Th>Not</Th>
            <Th>Durum</Th>
            <Th>İşlemler</Th>
          </tr>
        </thead>
        <tbody>
          {reservations.map(reservation => (
            <Tr key={reservation.id}>
              <Td>
                {reservation.memberDetails?.firstName} {reservation.memberDetails?.lastName}
                <br />
                <small style={{ color: colors.textLight }}>
                  {reservation.memberDetails?.email}
                </small>
              </Td>
              <Td>{reservation.classDetails?.title || 'N/A'}</Td>
              <Td>
                {reservation.classDetails?.date} <br />
                {reservation.classDetails?.time}
              </Td>
              <Td>{reservation.notes || '-'}</Td>
              <Td>
                <Badge status={deriveStatusKey(reservation)}>
                  {getStatusLabel(reservation)}
                </Badge>
              </Td>
              <Td>
                <Actions>
                  {reservation.status === 'pending' && (
                    <>
                      <Button 
                        size="small" 
                        variant="success"
                        onClick={() => handleApprove(reservation.id)}
                      >
                        Onayla
                      </Button>
                      <Button 
                        size="small" 
                        variant="danger"
                        onClick={() => openRejectModal(reservation)}
                      >
                        Reddet
                      </Button>
                      <Button 
                        size="small"
                        onClick={() => openMoveModal(reservation)}
                      >
                        Taşı
                      </Button>
                    </>
                  )}
                  {reservation.status === 'approved' && (
                    <Button 
                      size="small"
                      onClick={() => openMoveModal(reservation)}
                    >
                      Taşı
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    variant="danger"
                    onClick={() => handleCancel(reservation.id)}
                  >
                    İptal
                  </Button>
                </Actions>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {/* Mobile Card Layout */}
      {reservations.map(reservation => (
        <MobileCard key={reservation.id}>
          <MobileCardHeader>
            <div>
              <h3 style={{ margin: 0, color: colors.primary, fontSize: '16px' }}>
                {reservation.memberDetails?.firstName} {reservation.memberDetails?.lastName}
              </h3>
              <p style={{ margin: '4px 0 0 0', color: colors.textLight, fontSize: '12px' }}>
                {reservation.memberDetails?.email}
              </p>
            </div>
            <Badge status={deriveStatusKey(reservation)}>
              {getStatusLabel(reservation)}
            </Badge>
          </MobileCardHeader>
          
          <MobileCardInfo>
            <MobileCardLabel>Ders:</MobileCardLabel>
            <MobileCardValue>{reservation.classDetails?.title || 'N/A'}</MobileCardValue>
            
            <MobileCardLabel>Tarih:</MobileCardLabel>
            <MobileCardValue>{reservation.classDetails?.date}</MobileCardValue>
            
            <MobileCardLabel>Saat:</MobileCardLabel>
            <MobileCardValue>{reservation.classDetails?.time}</MobileCardValue>
            
            <MobileCardLabel>Not:</MobileCardLabel>
            <MobileCardValue>{reservation.notes || '-'}</MobileCardValue>
          </MobileCardInfo>
          
          <MobileCardActions>
            {reservation.status === 'pending' && (
              <>
                <Button 
                  size="small" 
                  variant="success"
                  onClick={() => handleApprove(reservation.id)}
                >
                  Onayla
                </Button>
                <Button 
                  size="small" 
                  variant="danger"
                  onClick={() => openRejectModal(reservation)}
                >
                  Reddet
                </Button>
                <Button 
                  size="small"
                  onClick={() => openMoveModal(reservation)}
                >
                  Taşı
                </Button>
              </>
            )}
            {reservation.status === 'approved' && (
              <Button 
                size="small"
                onClick={() => openMoveModal(reservation)}
              >
                Taşı
              </Button>
            )}
            <Button 
              size="small" 
              variant="danger"
              onClick={() => handleCancel(reservation.id)}
            >
              İptal
            </Button>
          </MobileCardActions>
        </MobileCard>
      ))}

      {/* Reject Modal */}
      <Modal
        isOpen={modalOpen && modalType === 'reject'}
        onClose={() => setModalOpen(false)}
        title="Rezervasyonu Reddet"
      >
        <FormGroup>
          <Label>Reddetme Sebebi (Opsiyonel)</Label>
          <TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Neden reddedildiğini açıklayın..."
          />
        </FormGroup>
        <ButtonGroup>
          <Button variant="danger" fullWidth onClick={handleReject}>
            Reddet
          </Button>
          <Button variant="secondary" fullWidth onClick={() => setModalOpen(false)}>
            İptal
          </Button>
        </ButtonGroup>
      </Modal>

      {/* Move Modal */}
      <Modal
        isOpen={modalOpen && modalType === 'move'}
        onClose={() => setModalOpen(false)}
        title="Rezervasyonu Taşı"
      >
        <FormGroup>
          <Label>Yeni Ders</Label>
          <Select
            value={moveToClassId}
            onChange={(e) => setMoveToClassId(e.target.value)}
            required
          >
            <option value="">Ders seçin...</option>
            {classes
              .filter(c => c.id !== selectedReservation?.classId && !c.isFull)
              .map(classItem => (
                <option key={classItem.id} value={classItem.id}>
                  {classItem.title} - {classItem.date} {classItem.time}
                  ({classItem.availableSpots} yer mevcut)
                </option>
              ))}
          </Select>
        </FormGroup>
        <ButtonGroup>
          <Button fullWidth onClick={handleMove}>
            Taşı
          </Button>
          <Button variant="secondary" fullWidth onClick={() => setModalOpen(false)}>
            İptal
          </Button>
        </ButtonGroup>
      </Modal>
    </Container>
  );
};

